import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Card, CardContent, Badge } from "@learn-me/ui";
import { bundlesApi } from "@/features/bundles/api/bundlesApi";
import { formatCurrency } from "@learn-me/utils";
import {
  Plus,
  Edit,
  Trash2,
  Package,
  Eye,
  EyeOff,
  Percent,
  Copy,
} from "lucide-react";
import toast from "react-hot-toast";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { handleApiError } from "@/lib/errorHandler";
import { normalizeImageUrl } from "@/lib/imageUrl";

export default function BundlesPage() {
  const [page, setPage] = useState(1);
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    bundle: { id: string; name: string } | null;
  }>({
    isOpen: false,
    bundle: null,
  });
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin-bundles", page],
    queryFn: () => bundlesApi.listAll(page),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => bundlesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-bundles"] });
      toast.success("Paquete eliminado");
      setDeleteDialog({ isOpen: false, bundle: null });
    },
    onError: (error) => {
      handleApiError(error, "Error al eliminar el paquete");
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: (id: string) => bundlesApi.duplicate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-bundles"] });
      toast.success("Paquete duplicado exitosamente");
    },
    onError: (error) => {
      handleApiError(error, "Error al duplicar el paquete");
    },
  });

  const handleDeleteClick = (bundle: { id: string; name: string }) => {
    setDeleteDialog({ isOpen: true, bundle });
  };

  const handleConfirmDelete = () => {
    if (deleteDialog.bundle) {
      deleteMutation.mutate(deleteDialog.bundle.id);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Paquetes de Cursos</h1>
          <p className="text-muted-foreground">
            Agrupa cursos en paquetes con descuentos especiales.
          </p>
        </div>
        <Link to="/admin/bundles/new">
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            Nuevo Paquete
          </Button>
        </Link>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4 flex gap-4">
                <div className="w-40 h-24 bg-muted rounded" />
                <div className="flex-1 space-y-2">
                  <div className="h-5 bg-muted rounded w-1/2" />
                  <div className="h-4 bg-muted rounded w-1/4" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : !data?.items || data.items.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No hay paquetes</h2>
            <p className="text-muted-foreground mb-6">
              Crea tu primer paquete de cursos.
            </p>
            <Link to="/admin/bundles/new">
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Crear Paquete
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="space-y-4">
            {data.items.map((bundle) => (
              <Card key={bundle.id}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-40 h-24 bg-muted rounded overflow-hidden flex-shrink-0">
                    {bundle.thumbnailUrl ? (
                      <img
                        src={normalizeImageUrl(bundle.thumbnailUrl) ?? ""}
                        alt={bundle.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-100 to-primary-200">
                        <Package className="h-8 w-8 text-primary-600" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold truncate">{bundle.name}</h3>
                      {bundle.isPublished ? (
                        <Badge variant="success" className="flex-shrink-0">
                          <Eye className="h-3 w-3 mr-1" />
                          Publicado
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="flex-shrink-0">
                          <EyeOff className="h-3 w-3 mr-1" />
                          Borrador
                        </Badge>
                      )}
                      {bundle.hasDiscount && (
                        <Badge
                          variant="default"
                          className="bg-green-500 flex-shrink-0"
                        >
                          <Percent className="h-3 w-3 mr-1" />
                          Descuento
                        </Badge>
                      )}
                    </div>
                    {bundle.shortDescription && (
                      <p className="text-sm text-muted-foreground line-clamp-1 mb-2">
                        {bundle.shortDescription}
                      </p>
                    )}
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-muted-foreground">
                        {bundle.coursesCount} cursos
                      </span>
                      {bundle.hasDiscount && bundle.originalPrice ? (
                        <div className="flex items-center gap-2">
                          <span className="line-through text-muted-foreground">
                            {formatCurrency(
                              bundle.originalPrice,
                              bundle.currency,
                            )}
                          </span>
                          <span className="font-semibold text-green-600">
                            {formatCurrency(bundle.price ?? 0, bundle.currency)}
                          </span>
                        </div>
                      ) : bundle.price ? (
                        <span className="font-medium">
                          {formatCurrency(bundle.price, bundle.currency)}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => duplicateMutation.mutate(bundle.id)}
                      disabled={duplicateMutation.isPending}
                      title="Duplicar paquete"
                    >
                      <Copy className="h-4 w-4 text-green-600" />
                    </Button>
                    <Link to={`/admin/bundles/${bundle.id}/edit`}>
                      <Button
                        variant="outline"
                        size="sm"
                        title="Editar paquete"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </Link>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        handleDeleteClick({
                          id: bundle.id,
                          name: bundle.name,
                        })
                      }
                      disabled={deleteMutation.isPending}
                      title="Eliminar paquete"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {data.pagination.totalPages > 1 && (
            <div className="flex justify-center gap-2">
              <Button
                variant="outline"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Anterior
              </Button>
              <span className="flex items-center px-4">
                Página {page} de {data.pagination.totalPages}
              </span>
              <Button
                variant="outline"
                disabled={page === data.pagination.totalPages}
                onClick={() => setPage(page + 1)}
              >
                Siguiente
              </Button>
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, bundle: null })}
        onConfirm={handleConfirmDelete}
        title="Eliminar Paquete"
        description={`¿Estás seguro de eliminar "${deleteDialog.bundle?.name}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
